export type GeneratedProfileType = 'plant' | 'pest';

export type GenerationLogStage =
  | 'request_start'
  | 'cache_hit'
  | 'claude_call'
  | 'claude_response'
  | 'normalization'
  | 'validation'
  | 'kv_write'
  | 'timeout_warning';

export type GenerationErrorStage =
  | 'claude_call'
  | 'normalization'
  | 'validation'
  | 'kv_write'
  | 'unknown';

export interface GenerationStageEvent {
  stage: GenerationLogStage;
  type: GeneratedProfileType;
  name: string;
  id?: string;
  elapsedMs?: number;
  details?: Record<string, unknown>;
  level?: 'info' | 'warn' | 'error';
}

export interface GenerationErrorResponse {
  success: false;
  stage: GenerationErrorStage;
  message: string;
  details?: Record<string, unknown>;
}

export interface GenerationFailureContext {
  type: GeneratedProfileType;
  name: string;
  id?: string;
  elapsedMs?: number;
  lastStage?: GenerationLogStage;
}

export class GenerationPipelineError extends Error {
  stage: GenerationErrorStage;
  details?: Record<string, unknown>;
  statusCode?: number;
  classification?: string;

  constructor(
    stage: GenerationErrorStage,
    message: string,
    options: {
      details?: Record<string, unknown>;
      statusCode?: number;
      classification?: string;
    } = {},
  ) {
    super(message);
    this.name = 'GenerationPipelineError';
    this.stage = stage;
    this.details = options.details;
    this.statusCode = options.statusCode;
    this.classification = options.classification;
  }
}

export class GeneratedProfileRequestError extends Error {
  stage: GenerationErrorStage;
  details?: Record<string, unknown>;
  statusCode?: number;

  constructor(
    message: string,
    options: {
      stage?: GenerationErrorStage;
      details?: Record<string, unknown>;
      statusCode?: number;
    } = {},
  ) {
    super(message);
    this.name = 'GeneratedProfileRequestError';
    this.stage = options.stage ?? 'unknown';
    this.details = options.details;
    this.statusCode = options.statusCode;
  }
}

export function generationErrorDetails(error: unknown): Record<string, string> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      ...(error.stack ? { stack: error.stack.slice(0, 2000) } : {}),
    };
  }

  return { message: String(error) };
}

export function logGenerationStage(event: GenerationStageEvent): void {
  const entry = {
    stage: event.stage,
    type: event.type,
    name: event.name,
    ...(event.id ? { id: event.id } : {}),
    ...(typeof event.elapsedMs === 'number' ? { elapsedMs: event.elapsedMs } : {}),
    ...(event.details ? { details: event.details } : {}),
  };

  if (event.level === 'warn') {
    console.warn('[generate-profile]', entry);
    return;
  }

  if (event.level === 'error') {
    console.error('[generate-profile]', entry);
    return;
  }

  console.info('[generate-profile]', entry);
}

export function isTimeoutLikeError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const normalized = `${error.name} ${error.message}`.toLowerCase();
  return normalized.includes('timeout') || normalized.includes('timed out') || normalized.includes('abort');
}

function defaultClassificationForStage(stage: GenerationErrorStage): string {
  if (stage === 'validation') return 'validation_error';
  if (stage === 'claude_call') return 'api_failure';
  if (stage === 'kv_write') return 'internal_error';
  return 'internal_error';
}

function defaultStatusCodeForStage(stage: GenerationErrorStage): number {
  if (stage === 'validation') return 400;
  if (stage === 'claude_call') return 502;
  if (stage === 'kv_write') return 500;
  return 500;
}

export function classifyGenerationFailure(
  error: unknown,
  context: GenerationFailureContext,
): { status: number; payload: GenerationErrorResponse } {
  if (error instanceof GenerationPipelineError) {
    const timeout = isTimeoutLikeError(error);
    const classification = timeout
      ? 'timeout'
      : error.classification ?? defaultClassificationForStage(error.stage);
    const status = timeout ? 504 : error.statusCode ?? defaultStatusCodeForStage(error.stage);

    return {
      status,
      payload: {
        success: false,
        stage: error.stage,
        message: error.message,
        details: {
          type: context.type,
          name: context.name,
          ...(context.id ? { id: context.id } : {}),
          ...(typeof context.elapsedMs === 'number' ? { elapsedMs: context.elapsedMs } : {}),
          ...(context.lastStage ? { lastStage: context.lastStage } : {}),
          classification,
          ...(error.details ?? {}),
        },
      },
    };
  }

  const timeout = isTimeoutLikeError(error);
  const message = error instanceof Error ? error.message : 'Unexpected generation failure.';

  return {
    status: timeout ? 504 : 500,
    payload: {
      success: false,
      stage: 'unknown',
      message,
      details: {
        type: context.type,
        name: context.name,
        ...(context.id ? { id: context.id } : {}),
        ...(typeof context.elapsedMs === 'number' ? { elapsedMs: context.elapsedMs } : {}),
        ...(context.lastStage ? { lastStage: context.lastStage } : {}),
        classification: timeout ? 'timeout' : 'internal_error',
        ...generationErrorDetails(error),
      },
    },
  };
}

function humanizeStage(stage: GenerationErrorStage): string {
  if (stage === 'claude_call') return 'Claude call';
  if (stage === 'kv_write') return 'storage';
  return stage;
}

export function formatGenerationFailureMessage(error: {
  stage?: GenerationErrorStage;
  message?: string;
}): string {
  const stage = error.stage ?? 'unknown';
  const message = error.message?.trim() || 'Generation failed.';
  return `Generation failed during ${humanizeStage(stage)}: ${message}`;
}
